import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Animated,
  RefreshControl,
  Modal,
  TextInput,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import Svg, { Rect, Line, Text as SvgText, Defs, LinearGradient, Stop } from 'react-native-svg';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useQuotes } from '../../hooks/useQuotes';
import { useFollowUps } from '../../hooks/useFollowUps';
import { useActivity } from '../../hooks/useActivity';
import { DashboardSkeleton } from '../../components/Skeleton';
import { haptics } from '../../lib/haptics';
import { Feather } from '@expo/vector-icons';
import { useAppTheme } from '../../contexts/ThemeContext';
import { useBusiness } from '../../contexts/BusinessContext';
import BusinessSwitcher from '../../components/BusinessSwitcher';

const ACTIVITY_CONFIG = {
  quote: {
    draft:    { icon: '📋', label: 'Quote drafted' },
    sent:     { icon: '📤', label: 'Quote sent' },
    accepted: { icon: '✅', label: 'Quote accepted' },
    declined: { icon: '❌', label: 'Quote declined' },
  },
  invoice: {
    unpaid:  { icon: '🧾', label: 'Invoice created' },
    paid:    { icon: '💰', label: 'Invoice paid' },
    overdue: { icon: '⚠️', label: 'Invoice overdue' },
  },
};

function timeAgo(ts) {
  const seconds = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatCurrency(n) {
  if (!n && n !== 0) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
}


function formatTimeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return mins <= 1 ? 'just now' : `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getActivityIcon(item) {
  if (item.type === 'invoice') {
    if (item.status === 'paid') return { featherIcon: 'dollar-sign', iconColor: '#22C55E' };
    if (item.status === 'overdue') return { featherIcon: 'alert-circle', iconColor: '#F43F5E' };
    return { featherIcon: 'file-text', iconColor: '#F59E0B' };
  }
  if (item.status === 'accepted') return { featherIcon: 'check-circle', iconColor: '#22C55E' };
  if (item.status === 'sent') return { featherIcon: 'send', iconColor: '#4F7EF8' };
  if (item.status === 'declined') return { featherIcon: 'x-circle', iconColor: '#F43F5E' };
  return { featherIcon: 'file-text', iconColor: '#94A3B8' };
}

// Animated number that counts up on mount
function AnimatedNumber({ value, prefix = '', suffix = '', style, currency = true }) {
  const animVal = useRef(new Animated.Value(0)).current;
  const [displayed, setDisplayed] = useState('0');

  useEffect(() => {
    const numericVal = parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0;
    animVal.setValue(0);
    Animated.timing(animVal, {
      toValue: numericVal,
      duration: 800,
      useNativeDriver: false,
    }).start();
    const listener = animVal.addListener(({ value: v }) => {
      setDisplayed(
        currency
          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v)
          : Math.round(v).toString()
      );
    });
    return () => animVal.removeListener(listener);
  }, [value, currency]);

  return <Text style={style} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.6}>{prefix}{displayed}{suffix}</Text>;
}

// Card that slides + fades in on mount
function AnimatedCard({ children, style, delay = 0 }) {
  const anim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.spring(anim, {
      toValue: 1,
      delay,
      tension: 60,
      friction: 10,
      useNativeDriver: true,
    }).start();
  }, []);

  return (
    <Animated.View
      style={[
        style,
        {
          opacity: anim,
          transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [24, 0] }) }],
        },
      ]}
    >
      {children}
    </Animated.View>
  );
}

// AI-generated smart insights based on data
function InsightCard({ stats, followUps, navigation }) {
  const { colors, spacing, typography, radius } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, spacing, typography, radius), [colors]);

  const insights = [];

  if (stats) {
    if (stats.pendingCount > 0) {
      const oldest = stats.oldestPendingDays;
      if (oldest > 7) {
        insights.push({
          icon: '⏳',
          color: '#FBBF24',
          text: `${stats.pendingCount} quote${stats.pendingCount > 1 ? 's' : ''} pending — oldest is ${oldest} days old. Time to follow up.`,
          action: () => navigation.navigate('Quotes', { screen: 'QuoteList', params: { filter: 'sent' } }),
          actionLabel: 'View',
        });
      }
    }

    if (stats.acceptRate < 40 && stats.totalSent > 3) {
      insights.push({
        icon: '📉',
        color: '#F87171',
        text: `Acceptance rate is ${stats.acceptRate}%. Try lowering prices or adding more detail to quotes.`,
        action: null,
      });
    }

    if (stats.acceptRate > 70 && stats.totalSent > 3) {
      insights.push({
        icon: '🔥',
        color: '#34D399',
        text: `${stats.acceptRate}% acceptance rate — you're crushing it. Consider raising your prices.`,
        action: null,
      });
    }

    if ((stats.collectedThisMonth || 0) === 0 && (stats.pipelineThisMonth || 0) > 0) {
      insights.push({
        icon: '💰',
        color: '#4F8EF7',
        text: `You have ${formatCurrency(stats.pipelineThisMonth)} in accepted quotes. Create invoices to get paid.`,
        action: () => navigation.navigate('Invoices'),
        actionLabel: 'Invoices',
      });
    }
  }

  if (followUps.length > 2) {
    insights.push({
      icon: '📞',
      color: '#FBBF24',
      text: `${followUps.length} follow-ups overdue. Clients forget — a quick message could win back jobs.`,
      action: null,
    });
  }

  if (insights.length === 0) return null;

  const insight = insights[0];

  return (
    <AnimatedCard delay={100} style={[styles.insightCard, { borderColor: `${insight.color}30` }]}>
      <View style={[styles.insightIconBox, { backgroundColor: `${insight.color}15` }]}>
        <Text style={styles.insightIcon}>{insight.icon}</Text>
      </View>
      <Text style={styles.insightText}>{insight.text}</Text>
      {insight.action && (
        <TouchableOpacity
          style={[styles.insightAction, { borderColor: `${insight.color}40` }]}
          onPress={() => { haptics.light(); insight.action(); }}
          activeOpacity={0.7}
        >
          <Text style={[styles.insightActionText, { color: insight.color }]}>{insight.actionLabel} →</Text>
        </TouchableOpacity>
      )}
    </AnimatedCard>
  );
}

function BarChart({ data }) {
  const { colors } = useAppTheme();
  const [chartWidth, setChartWidth] = useState(300);

  const yAxisW = 44;
  const bottomH = 28;
  const plotW = chartWidth - yAxisW;

  const maxVal = Math.max(...data.map((d) => d.total), 1);
  const gridMax = Math.ceil(maxVal / 1000) * 1000 || 1000;
  const chartH = Math.min(200, Math.max(70, Math.log10(Math.max(gridMax, 100)) * 45));
  const plotH = chartH;
  const gridLines = [0.5, 1].map((t) => ({ value: gridMax * t, y: plotH - plotH * t }));

  const barCount = data.length;
  const barW = Math.min(28, (plotW / barCount) * 0.55);
  const slotW = plotW / barCount;

  // Theme-aware colors for SVG (must be plain strings, not rgba objects)
  const gridLineColor = colors.border;
  const axisLabelColor = colors.textMuted;
  const labelActive = colors.text;
  const labelMuted = colors.textMuted;
  const emptyBarColor = colors.border;

  return (
    <View onLayout={(e) => setChartWidth(e.nativeEvent.layout.width)}>
      <Svg width={chartWidth} height={chartH + bottomH}>
        <Defs>
          <LinearGradient id="realized" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor="#34D399" stopOpacity="1" />
            <Stop offset="1" stopColor="#059669" stopOpacity="1" />
          </LinearGradient>
          <LinearGradient id="pipeline" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor="#6BA8FF" stopOpacity="1" />
            <Stop offset="1" stopColor="#3A6FD4" stopOpacity="1" />
          </LinearGradient>
          <LinearGradient id="realizedMuted" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor="#34D399" stopOpacity="0.4" />
            <Stop offset="1" stopColor="#059669" stopOpacity="0.2" />
          </LinearGradient>
          <LinearGradient id="pipelineMuted" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor="#4F8EF7" stopOpacity="0.4" />
            <Stop offset="1" stopColor="#4F8EF7" stopOpacity="0.15" />
          </LinearGradient>
        </Defs>

        {gridLines.map(({ value, y }) => (
          <React.Fragment key={value}>
            <Line x1={yAxisW} y1={y} x2={chartWidth} y2={y} stroke={gridLineColor} strokeWidth={1} />
            <SvgText x={yAxisW - 6} y={y + 4} fontSize={9} fill={axisLabelColor} textAnchor="end">
              {formatCurrency(value)}
            </SvgText>
          </React.Fragment>
        ))}
        <Line x1={yAxisW} y1={chartH} x2={chartWidth} y2={chartH} stroke={gridLineColor} strokeWidth={1} />

        {data.map((item, i) => {
          const isLast = i === data.length - 1;
          const realized = item.realized || 0;
          const unrealized = item.unrealized || 0;
          const total = realized + unrealized;
          const totalH = total > 0 ? Math.max((total / gridMax) * plotH, 4) : 0;
          const realizedH = total > 0 ? (realized / total) * totalH : 0;
          const unrealizedH = totalH - realizedH;
          const slotX = yAxisW + i * slotW;
          const x = slotX + (slotW - barW) / 2;
          const realizedY = chartH - realizedH;
          const unrealizedY = chartH - totalH;

          return (
            <React.Fragment key={item.month}>
              {totalH === 0 ? (
                <Rect x={x} y={chartH - 3} width={barW} height={3} rx={2} fill={emptyBarColor} />
              ) : (
                <>
                  {unrealizedH > 0 && (
                    <Rect x={x} y={unrealizedY} width={barW} height={unrealizedH} rx={5}
                      fill={isLast ? 'url(#pipeline)' : 'url(#pipelineMuted)'} />
                  )}
                  {realizedH > 0 && (
                    <Rect x={x} y={realizedY} width={barW} height={realizedH}
                      rx={unrealizedH > 0 ? 0 : 5}
                      fill={isLast ? 'url(#realized)' : 'url(#realizedMuted)'} />
                  )}
                </>
              )}
              {isLast && total > 0 && (
                <SvgText x={x + barW / 2} y={unrealizedY - 6} fontSize={9} fill={labelActive} textAnchor="middle" fontWeight="600">
                  {formatCurrency(total)}
                </SvgText>
              )}
              <SvgText
                x={x + barW / 2} y={chartH + 18} fontSize={10}
                fill={isLast ? labelActive : labelMuted}
                textAnchor="middle" fontWeight={isLast ? '600' : '400'}
              >
                {item.month}
              </SvgText>
            </React.Fragment>
          );
        })}
      </Svg>
    </View>
  );
}

export default function DashboardScreen({ navigation }) {
  const { colors, spacing, typography, radius } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, spacing, typography, radius), [colors]);

  const { user } = useAuth();
  const { activeBusiness } = useBusiness();
  const { getQuoteStats } = useQuotes();
  const { getFollowUps, completeFollowUp } = useFollowUps();
  const { getActivity } = useActivity();
  const [stats, setStats] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Profile sheet
  const [showProfile, setShowProfile] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileBusiness, setProfileBusiness] = useState('');
  const [profileSaved, setProfileSaved] = useState('');

  useEffect(() => {
    if (!showProfile || !user) return;
    async function loadProfile() {
      const name = user.user_metadata?.full_name || '';
      setProfileName(name);
      try {
        const { data } = await supabase
          .from('user_settings')
          .select('business_name')
          .eq('id', user.id)
          .maybeSingle();
        setProfileBusiness(data?.business_name || '');
      } catch {}
    }
    loadProfile();
  }, [showProfile, user]);

  async function saveProfileField(field, value) {
    if (!user) return;
    try {
      if (field === 'name') {
        await supabase.auth.updateUser({ data: { full_name: value } });
      }
      if (field === 'business') {
        await supabase.from('user_settings').upsert(
          { id: user.id, business_name: value },
          { onConflict: 'id' },
        );
      }
      setProfileSaved('✓ Saved');
      setTimeout(() => setProfileSaved(''), 2000);
    } catch {}
  }

  const displayName =
    user?.user_metadata?.full_name?.split(' ')[0] ||
    user?.email?.split('@')[0] ||
    'there';

  const load = useCallback(async () => {
    if (!user) return;
    try {
      setLoadError(null);
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();

      const bizId = activeBusiness?.id;
      const acceptedQ = supabase.from('quotes').select('id, total, created_at').eq('user_id', user.id).eq('status', 'accepted');
      const invoicesQ = supabase.from('invoices').select('total, created_at, quote_id').eq('user_id', user.id).eq('status', 'paid');
      const sentQ = supabase.from('quotes').select('id, created_at').eq('user_id', user.id).eq('status', 'sent');
      if (bizId) { acceptedQ.eq('business_id', bizId); invoicesQ.eq('business_id', bizId); sentQ.eq('business_id', bizId); }

      const [statsData, followUpsData, acceptedRes, invoicesRes, sentRes, activityData] = await Promise.all([
        getQuoteStats(user.id, bizId),
        getFollowUps({ userId: user.id, pendingOnly: true }),
        acceptedQ,
        invoicesQ,
        sentQ,
        getActivity(user.id, 10),
      ]);

      setStats(statsData);
      setActivity(activityData);

      const acceptedQuotes = acceptedRes.data || [];
      const paidInvoices = invoicesRes.data || [];
      const sentQuotes = sentRes.data || [];

      const paidQuoteIds = new Set(paidInvoices.map((inv) => inv.quote_id).filter(Boolean));

      const unrealizedThisMonth = acceptedQuotes
        .filter((q) => !paidQuoteIds.has(q.id) && q.created_at >= monthStart && q.created_at <= monthEnd)
        .reduce((sum, q) => sum + (q.total || 0), 0);
      const paidThisMonthTotal = paidInvoices
        .filter((inv) => inv.created_at >= monthStart && inv.created_at <= monthEnd)
        .reduce((sum, inv) => sum + (inv.total || 0), 0);

      // Calculate oldest pending quote in days
      const oldestPendingDays = sentQuotes.length > 0
        ? Math.max(...sentQuotes.map((q) => {
            const days = Math.floor((Date.now() - new Date(q.created_at)) / (1000 * 60 * 60 * 24));
            return days;
          }))
        : 0;

      const monthly = [];
      for (let i = 5; i >= 0; i--) {
        const barDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const barEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59);
        const barStart = barDate.toISOString();
        const barEndStr = barEnd.toISOString();
        const unrealized = acceptedQuotes
          .filter((q) => !paidQuoteIds.has(q.id) && q.created_at >= barStart && q.created_at <= barEndStr)
          .reduce((sum, q) => sum + (q.total || 0), 0);
        const realized = paidInvoices
          .filter((inv) => inv.created_at >= barStart && inv.created_at <= barEndStr)
          .reduce((sum, inv) => sum + (inv.total || 0), 0);
        monthly.push({
          month: barDate.toLocaleString('default', { month: 'short' }),
          realized, unrealized, total: unrealized + realized,
        });
      }

      const collectedTotal = paidInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);

      setStats((prev) => ({
        ...prev,
        monthly,
        collectedThisMonth: paidThisMonthTotal,
        collectedTotal,
        pipelineThisMonth: unrealizedThisMonth,
        oldestPendingDays,
        totalSent: sentQuotes.length,
      }));

      const today = new Date().toISOString().split('T')[0];
      setFollowUps(followUpsData.filter((f) => f.remind_at <= today));
    } catch {
      setLoadError('Could not load dashboard data. Pull down to refresh.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, activeBusiness, getQuoteStats, getFollowUps]);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  function onRefresh() {
    setRefreshing(true);
    load();
  }

  const monthName = new Date().toLocaleString('default', { month: 'long' }).toUpperCase();
  const acceptPct = Math.min(100, stats?.acceptRate || 0);

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.accent} />
        }
      >
        {/* Header */}
        <AnimatedCard delay={0} style={styles.header}>
          <BusinessSwitcher onAddNew={() => navigation.navigate('AddBusiness')} />
          <View style={styles.headerActions}>
            <TouchableOpacity
              style={styles.headerBtn}
              onPress={() => navigation.navigate('HelpModal')}
              activeOpacity={0.7}
            >
              <Feather name="life-buoy" size={17} color={colors.textMuted} />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.avatar}
              onPress={() => { haptics.light(); setShowProfile(true); }}
              activeOpacity={0.7}
            >
              <Text style={styles.avatarText}>{displayName.charAt(0).toUpperCase()}</Text>
            </TouchableOpacity>
          </View>
        </AnimatedCard>

        {/* Follow-up Reminders */}
        {followUps.length > 0 && (
          <AnimatedCard delay={40} style={styles.remindersCard}>
            <View style={styles.remindersHeader}>
              <Feather name="clock" size={13} color="#F59E0B" />
              <Text style={styles.remindersTitle}>{followUps.length} follow-up{followUps.length > 1 ? 's' : ''} due</Text>
            </View>
            {followUps.map((f) => (
              <View key={f.id} style={styles.reminderRow}>
                <View style={styles.reminderLeft}>
                  <Text style={styles.reminderClient}>{f.client_name}</Text>
                  <Text style={styles.reminderNote}>{f.note}</Text>
                </View>
                <TouchableOpacity
                  style={styles.reminderDone}
                  onPress={async () => {
                    haptics.success();
                    await completeFollowUp(f.id);
                    setFollowUps((prev) => prev.filter((x) => x.id !== f.id));
                  }}
                  activeOpacity={0.7}
                >
                  <Feather name="check" size={11} color={colors.success} />
                  <Text style={styles.reminderDoneText}>Done</Text>
                </TouchableOpacity>
              </View>
            ))}
          </AnimatedCard>
        )}

        {/* Hero Revenue Card */}
        {!loading && (
          <AnimatedCard delay={50} style={styles.heroCard}>
            <View style={styles.heroTop}>
              <Text style={styles.heroLabel}>{monthName} · COLLECTED</Text>
              <View style={styles.heroLivePill}>
                <View style={styles.heroLiveDot} />
                <Text style={styles.heroLiveText}>Live</Text>
              </View>
            </View>
            <AnimatedNumber
              value={stats?.collectedThisMonth || 0}
              style={styles.heroValue}
            />
            {(stats?.pipelineThisMonth || 0) > 0 && (
              <Text style={styles.heroPipelineHint}>
                + {formatCurrency(stats.pipelineThisMonth)} in pipeline
              </Text>
            )}
            <View style={styles.heroBar}>
              <Animated.View style={[styles.heroBarFill, { width: `${acceptPct}%` }]} />
            </View>
            <Text style={styles.heroBarLabel}>
              {stats?.acceptRate || 0}% accept rate · {stats?.pendingCount || 0} quote{(stats?.pendingCount || 0) !== 1 ? 's' : ''} pending
            </Text>
          </AnimatedCard>
        )}

        {/* CTA Buttons */}
        <AnimatedCard delay={80} style={styles.ctaRow}>
          <TouchableOpacity
            style={[styles.ctaBtn, styles.ctaBtnPrimary]}
            onPress={() => { haptics.medium(); navigation.navigate('Quotes', { screen: 'NewQuote' }); }}
            activeOpacity={0.8}
          >
            <View style={styles.ctaIconWrap}>
              <Feather name="zap" size={20} color="#fff" />
            </View>
            <Text style={styles.ctaBtnTitle}>AI Quote</Text>
            <Text style={[styles.ctaBtnSub, { color: 'rgba(255,255,255,0.6)' }]}>Full wizard</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.ctaBtn, styles.ctaBtnSecondary]}
            onPress={() => { haptics.light(); navigation.navigate('Quotes', { screen: 'QuickQuote' }); }}
            activeOpacity={0.8}
          >
            <View style={styles.ctaIconWrapSecondary}>
              <Feather name="edit-3" size={20} color={colors.textSecondary} />
            </View>
            <Text style={[styles.ctaBtnTitle, { color: colors.text }]}>Quick Quote</Text>
            <Text style={[styles.ctaBtnSub, { color: colors.textMuted }]}>Manual entry</Text>
          </TouchableOpacity>
        </AnimatedCard>

        {/* Quick Actions */}
        <AnimatedCard delay={100} style={styles.quickActions}>
          {[
            { icon: 'clipboard', label: 'Invoice', onPress: () => navigation.navigate('Invoices') },
            { icon: 'users', label: 'Clients', onPress: () => navigation.navigate('Clients') },
            { icon: 'tool', label: 'Tools', onPress: () => navigation.navigate('Tools') },
            { icon: 'search', label: 'Search', onPress: () => navigation.navigate('Search') },
          ].map(({ icon, label, onPress }) => (
            <TouchableOpacity key={label} style={styles.quickActionBtn} onPress={() => { haptics.light(); onPress(); }} activeOpacity={0.7}>
              <View style={styles.quickActionIcon}>
                <Feather name={icon} size={18} color={colors.textMuted} />
              </View>
              <Text style={styles.quickActionLabel}>{label}</Text>
            </TouchableOpacity>
          ))}
        </AnimatedCard>

        {/* Stats + Chart + Insights */}
        {loading ? (
          <DashboardSkeleton />
        ) : loadError ? (
          <View style={styles.errorBanner}>
            <Text style={styles.errorBannerText}>{loadError}</Text>
          </View>
        ) : (
          <>
            {/* Smart Insight */}
            {stats && (
              <InsightCard stats={stats} followUps={followUps} navigation={navigation} />
            )}

            {/* Stats Row */}
            <AnimatedCard delay={150} style={styles.statsRow}>
              <StatCard
                label="Total Quoted"
                value={formatCurrency(stats?.totalQuoted)}
                numericValue={stats?.totalQuoted || 0}
                sub={`${stats?.acceptRate || 0}% accepted`}
              />
              <StatCard
                label="Collected"
                value={formatCurrency(stats?.collectedTotal)}
                numericValue={stats?.collectedTotal || 0}
                sub="all time"
                accent
              />
              <StatCard
                label="Pending"
                value={`${stats?.pendingCount || 0}`}
                numericValue={stats?.pendingCount || 0}
                sub="awaiting"
                warning={stats?.pendingCount > 0}
                currency={false}
              />
            </AnimatedCard>

            {/* Revenue Chart */}
            {stats?.monthly && (
              <AnimatedCard delay={200} style={styles.chartCard}>
                <View style={styles.chartHeader}>
                  <View>
                    <Text style={styles.chartTitle}>6-Month Revenue</Text>
                    <Text style={styles.chartSub}>Collected vs. pipeline</Text>
                  </View>
                  <View style={styles.revenuePill}>
                    <View style={[styles.pillDot, { backgroundColor: '#22C55E' }]} />
                    <Text style={styles.pillValue}>{formatCurrency(stats.collectedThisMonth || 0)}</Text>
                    <Text style={styles.pillLabel}> this month</Text>
                  </View>
                </View>
                <View style={styles.chartArea}>
                  <BarChart data={stats.monthly} />
                </View>
                <View style={styles.chartLegend}>
                  <View style={styles.legendItem}>
                    <View style={[styles.legendDot, { backgroundColor: '#22C55E' }]} />
                    <Text style={styles.legendLabel}>Collected</Text>
                  </View>
                  <View style={styles.legendItem}>
                    <View style={[styles.legendDot, { backgroundColor: '#4F7EF8' }]} />
                    <Text style={styles.legendLabel}>Pipeline</Text>
                  </View>
                </View>
              </AnimatedCard>
            )}

            {/* Activity Feed */}
            {activity.length > 0 && (
              <>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>Recent Activity</Text>
                </View>
                <AnimatedCard delay={250} style={styles.activityCard}>
                  {activity.slice(0, 6).map((item, i) => {
                    const isLast = i === Math.min(activity.length, 6) - 1;
                    const { featherIcon, iconColor } = getActivityIcon(item);
                    const label = item.type === 'invoice'
                      ? `Invoice · ${item.status}`
                      : `Quote · ${item.status}`;
                    return (
                      <TouchableOpacity
                        key={item.id}
                        style={[styles.activityRow, isLast && styles.activityRowLast]}
                        onPress={() => {
                          haptics.light();
                          if (item.type === 'quote') {
                            navigation.navigate('Quotes', { screen: 'QuoteDetail', params: { quoteId: item.quoteId } });
                          } else {
                            navigation.navigate('Invoices', { screen: 'InvoiceDetail', params: { invoiceId: item.invoiceId } });
                          }
                        }}
                        activeOpacity={0.7}
                      >
                        <View style={[styles.activityIconCircle, { backgroundColor: `${iconColor}18` }]}>
                          <Feather name={featherIcon} size={14} color={iconColor} />
                        </View>
                        <View style={styles.activityMeta}>
                          <Text style={styles.activityLabel}>{item.client}</Text>
                          <Text style={styles.activityClient}>{label}{item.detail ? ` · ${item.detail}` : ''}</Text>
                        </View>
                        <View style={styles.activityRight}>
                          {item.amount ? <Text style={styles.activityAmount}>{formatCurrency(item.amount)}</Text> : null}
                          <Text style={styles.activityTime}>{formatTimeAgo(item.ts)}</Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </AnimatedCard>
              </>
            )}

          </>
        )}
      </ScrollView>

      {/* Profile Sheet */}
      <Modal
        visible={showProfile}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowProfile(false)}
      >
        <SafeAreaView style={styles.profileSheet} edges={['top', 'bottom']}>
          {/* Header row */}
          <View style={styles.profileSheetHeader}>
            <Text style={styles.profileSheetTitle}>Profile</Text>
            <TouchableOpacity
              style={styles.profileSheetClose}
              onPress={() => setShowProfile(false)}
              activeOpacity={0.7}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Text style={styles.profileSheetCloseText}>✕</Text>
            </TouchableOpacity>
          </View>

          {/* Avatar */}
          <View style={styles.profileSheetAvatar}>
            <Text style={styles.profileSheetAvatarText}>
              {(profileName || displayName).charAt(0).toUpperCase()}
            </Text>
          </View>
          <Text style={styles.profileSheetName}>
            {profileName || displayName}
          </Text>
          <Text style={styles.profileSheetEmail}>{user?.email}</Text>

          {/* Saved indicator */}
          {profileSaved ? (
            <Text style={styles.profileSheetSaved}>{profileSaved}</Text>
          ) : null}

          {/* Fields */}
          <View style={styles.profileSheetField}>
            <Text style={styles.profileSheetLabel}>Your Name</Text>
            <TextInput
              style={styles.profileSheetInput}
              value={profileName}
              onChangeText={setProfileName}
              placeholder="Full name"
              placeholderTextColor={colors.textMuted}
              onBlur={() => saveProfileField('name', profileName)}
              autoCapitalize="words"
            />
          </View>

          <View style={styles.profileSheetField}>
            <Text style={styles.profileSheetLabel}>Business Name</Text>
            <TextInput
              style={styles.profileSheetInput}
              value={profileBusiness}
              onChangeText={setProfileBusiness}
              placeholder="Your Company LLC"
              placeholderTextColor={colors.textMuted}
              onBlur={() => saveProfileField('business', profileBusiness)}
              autoCapitalize="words"
            />
          </View>

          {/* Go to Settings link */}
          <TouchableOpacity
            style={styles.profileSheetSettingsLink}
            onPress={() => {
              setShowProfile(false);
              navigation.navigate('Settings');
            }}
            activeOpacity={0.7}
          >
            <Text style={styles.profileSheetSettingsLinkText}>Go to Settings →</Text>
          </TouchableOpacity>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

function StatCard({ label, value, numericValue, sub, accent, warning, currency = true }) {
  const { colors, spacing, typography, radius } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, spacing, typography, radius), [colors]);

  return (
    <View style={[styles.statCard, accent && styles.statCardAccent, warning && styles.statCardWarning]}>
      <AnimatedNumber
        value={numericValue}
        currency={currency}
        style={[
          styles.statValue,
          accent && { color: colors.success },
          warning && { color: colors.warning },
        ]}
      />
      <Text style={styles.statLabel}>{label}</Text>
      {sub ? (
        <Text style={[styles.statSub, accent && { color: colors.success }, warning && { color: colors.warning }]}>
          {sub}
        </Text>
      ) : null}
    </View>
  );
}

function createStyles(colors, spacing, typography, radius) {
  return StyleSheet.create({
    safe: { flex: 1, backgroundColor: colors.background },
    scroll: { flex: 1 },
    content: {
      paddingHorizontal: spacing.xl,
      paddingBottom: 120,
      ...(Platform.OS === 'web' && { maxWidth: 560, width: '100%', alignSelf: 'center' }),
    },
    errorBanner: {
      backgroundColor: colors.dangerLight, borderRadius: radius.md,
      borderWidth: 1, borderColor: colors.danger, padding: spacing.md, marginBottom: spacing.xl,
    },
    errorBannerText: { fontSize: typography.sizes.sm, color: colors.danger, textAlign: 'center' },
    header: {
      flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
      paddingTop: spacing.base, marginBottom: spacing.xl,
    },
    headerActions: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    headerBtn: {
      width: 36, height: 36, borderRadius: 18,
      backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border,
      alignItems: 'center', justifyContent: 'center',
    },
    avatar: {
      width: 38, height: 38, borderRadius: 19,
      backgroundColor: colors.accentLight, alignItems: 'center', justifyContent: 'center',
      borderWidth: 1, borderColor: 'rgba(79,126,248,0.3)',
    },
    avatarText: { fontSize: typography.sizes.sm, fontWeight: typography.weights.bold, color: colors.accent },
    heroCard: {
      backgroundColor: colors.surfaceElevated,
      borderRadius: radius.lg, borderWidth: 1,
      borderColor: 'rgba(34,197,94,0.14)',
      padding: spacing.xl, marginBottom: spacing.base,
    },
    heroTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs },
    heroLabel: { fontSize: typography.sizes.xs, fontWeight: '700', color: colors.textMuted, letterSpacing: 1.5 },
    heroLivePill: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(34,197,94,0.1)', paddingHorizontal: 8, paddingVertical: 3, borderRadius: radius.full },
    heroLiveDot: { width: 5, height: 5, borderRadius: 3, backgroundColor: '#22C55E' },
    heroLiveText: { fontSize: 10, color: '#22C55E', fontWeight: '700', letterSpacing: 0.5 },
    heroValue: { fontSize: 46, fontWeight: '800', color: colors.text, letterSpacing: -2, marginBottom: 4, marginTop: 4 },
    heroPipelineHint: { fontSize: typography.sizes.sm, color: colors.accent, marginBottom: spacing.md, fontWeight: '500' },
    heroBar: { height: 3, backgroundColor: colors.border, borderRadius: 2, marginBottom: spacing.xs, marginTop: spacing.sm, overflow: 'hidden' },
    heroBarFill: { height: 3, backgroundColor: '#22C55E', borderRadius: 2 },
    heroBarLabel: { fontSize: typography.sizes.xs, color: colors.textMuted },
    insightCard: {
      backgroundColor: 'rgba(255,255,255,0.04)',
      borderRadius: radius.lg, borderWidth: 1,
      padding: spacing.base, marginBottom: spacing.base,
      flexDirection: 'row', alignItems: 'center', gap: spacing.md,
    },
    insightIconBox: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
    insightIcon: { fontSize: 18 },
    insightText: { flex: 1, fontSize: typography.sizes.sm, color: colors.textSecondary, lineHeight: typography.sizes.sm * 1.5 },
    insightAction: {
      borderWidth: 1, borderRadius: radius.sm,
      paddingHorizontal: spacing.sm, paddingVertical: 4,
    },
    insightActionText: { fontSize: typography.sizes.xs, fontWeight: '600' },
    remindersCard: {
      backgroundColor: 'rgba(245,158,11,0.07)', borderRadius: radius.lg,
      borderWidth: 1, borderColor: 'rgba(245,158,11,0.18)',
      padding: spacing.base, marginBottom: spacing.base,
    },
    remindersHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: spacing.sm },
    remindersTitle: { fontSize: typography.sizes.sm, fontWeight: typography.weights.semibold, color: '#F59E0B' },
    reminderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: spacing.xs },
    reminderLeft: { flex: 1 },
    reminderClient: { fontSize: typography.sizes.sm, fontWeight: typography.weights.medium, color: colors.text },
    reminderNote: { fontSize: typography.sizes.xs, color: colors.textMuted, marginTop: 1 },
    reminderDone: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: 'rgba(34,197,94,0.12)', borderRadius: radius.sm, paddingHorizontal: spacing.sm, paddingVertical: 5, borderWidth: 1, borderColor: 'rgba(34,197,94,0.25)' },
    reminderDoneText: { fontSize: typography.sizes.xs, color: colors.success, fontWeight: typography.weights.semibold },
    ctaRow: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.base },
    ctaBtn: { flex: 1, borderRadius: radius.lg, padding: spacing.base, paddingVertical: spacing.lg, minHeight: Platform.OS === 'web' ? 80 : 112, alignItems: 'center' },
    ctaBtnPrimary: {
      backgroundColor: colors.accent,
      shadowColor: colors.accent, shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.4, shadowRadius: 16, elevation: 10,
    },
    ctaBtnSecondary: { backgroundColor: colors.surfaceElevated, borderWidth: 1, borderColor: colors.border },
    ctaIconWrap: { width: 40, height: 40, borderRadius: radius.md, backgroundColor: 'rgba(255,255,255,0.15)', alignItems: 'center', justifyContent: 'center', marginBottom: spacing.sm },
    ctaIconWrapSecondary: { width: 40, height: 40, borderRadius: radius.md, backgroundColor: colors.surfaceHighlight, alignItems: 'center', justifyContent: 'center', marginBottom: spacing.sm },
    ctaBtnTitle: { fontSize: typography.sizes.md, fontWeight: typography.weights.bold, color: colors.text, letterSpacing: -0.4, marginBottom: 3, textAlign: 'center' },
    ctaBtnSub: { fontSize: typography.sizes.xs, textAlign: 'center' },
    quickActions: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.xl },
    quickActionBtn: { flex: 1, alignItems: 'center', gap: 6 },
    quickActionIcon: {
      width: 50, height: 50, borderRadius: radius.lg,
      backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border,
      alignItems: 'center', justifyContent: 'center',
    },
    quickActionLabel: { fontSize: 10, color: colors.textMuted, fontWeight: '600', letterSpacing: 0.3, textTransform: 'uppercase' },
    statsRow: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.xl },
    statCard: { flex: 1, backgroundColor: colors.surface, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border, padding: spacing.md, alignItems: 'center' },
    statCardAccent: { borderColor: 'rgba(52,211,153,0.2)', backgroundColor: colors.successLight },
    statCardWarning: { borderColor: 'rgba(251,191,36,0.2)', backgroundColor: colors.warningLight },
    statValue: { fontSize: typography.sizes.xl, fontWeight: typography.weights.bold, color: colors.text, letterSpacing: -0.5 },
    statLabel: { fontSize: 10, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 },
    statSub: { fontSize: 10, color: colors.textMuted, marginTop: 1 },
    chartCard: { backgroundColor: colors.surface, borderRadius: radius.lg, borderWidth: 1, borderColor: colors.border, padding: spacing.base, marginBottom: spacing.xl },
    chartHeader: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.md },
    chartTitle: { fontSize: typography.sizes.base, fontWeight: typography.weights.bold, color: colors.text, letterSpacing: -0.3 },
    chartSub: { fontSize: typography.sizes.xs, color: colors.textMuted, marginTop: 2 },
    revenuePill: { flexDirection: 'row', alignItems: 'center', gap: 5 },
    pillDot: { width: 8, height: 8, borderRadius: 4 },
    pillValue: { fontSize: typography.sizes.sm, fontWeight: typography.weights.bold, color: colors.text, letterSpacing: -0.3 },
    pillLabel: { fontSize: typography.sizes.xs, color: colors.textMuted },
    chartLegend: { flexDirection: 'row', gap: spacing.base, marginTop: spacing.sm },
    legendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
    legendDot: { width: 8, height: 8, borderRadius: 4 },
    legendLabel: { fontSize: 10, color: colors.textMuted },
    chartArea: { marginHorizontal: -4 },
    sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
    sectionTitle: { fontSize: typography.sizes.base, fontWeight: typography.weights.semibold, color: colors.text },
    seeAll: { fontSize: typography.sizes.sm, color: colors.accent, fontWeight: typography.weights.medium },
    activityCard: { backgroundColor: colors.surface, borderRadius: radius.lg, borderWidth: 1, borderColor: colors.border, overflow: 'hidden' },
    activityRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: spacing.base, paddingVertical: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border, gap: spacing.sm },
    activityRowLast: { borderBottomWidth: 0 },
    activityIconCircle: { width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center' },
    activityMeta: { flex: 1 },
    activityLabel: { fontSize: typography.sizes.sm, fontWeight: typography.weights.medium, color: colors.text },
    activityClient: { fontSize: typography.sizes.xs, color: colors.textMuted, marginTop: 1 },
    activityRight: { alignItems: 'flex-end' },
    activityAmount: { fontSize: typography.sizes.sm, fontWeight: typography.weights.semibold, color: colors.text },
    activityTime: { fontSize: 10, color: colors.textMuted, marginTop: 1 },
    // Profile sheet
    profileSheet: {
      flex: 1,
      backgroundColor: colors.background,
      paddingHorizontal: spacing.xl,
    },
    profileSheetHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingTop: spacing.base,
      marginBottom: spacing.xl,
    },
    profileSheetTitle: {
      fontSize: typography.sizes.lg,
      fontWeight: typography.weights.bold,
      color: colors.text,
      letterSpacing: -0.4,
    },
    profileSheetClose: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    profileSheetCloseText: {
      fontSize: typography.sizes.sm,
      color: colors.textMuted,
      fontWeight: typography.weights.semibold,
    },
    profileSheetAvatar: {
      width: 72,
      height: 72,
      borderRadius: 36,
      backgroundColor: colors.accentLight,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 2,
      borderColor: 'rgba(79,142,247,0.3)',
      alignSelf: 'center',
      marginBottom: spacing.md,
    },
    profileSheetAvatarText: {
      fontSize: 28,
      fontWeight: typography.weights.bold,
      color: colors.accent,
    },
    profileSheetName: {
      fontSize: typography.sizes.lg,
      fontWeight: typography.weights.bold,
      color: colors.text,
      textAlign: 'center',
      letterSpacing: -0.3,
    },
    profileSheetEmail: {
      fontSize: typography.sizes.sm,
      color: colors.textMuted,
      textAlign: 'center',
      marginTop: 4,
      marginBottom: spacing.xl,
    },
    profileSheetSaved: {
      fontSize: typography.sizes.sm,
      color: colors.success,
      textAlign: 'center',
      marginBottom: spacing.md,
      fontWeight: typography.weights.medium,
    },
    profileSheetField: {
      marginBottom: spacing.base,
    },
    profileSheetLabel: {
      fontSize: typography.sizes.sm,
      fontWeight: typography.weights.medium,
      color: colors.textSecondary,
      marginBottom: spacing.xs + 2,
      letterSpacing: 0.3,
    },
    profileSheetInput: {
      backgroundColor: colors.surface,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: spacing.base,
      paddingVertical: spacing.md,
      fontSize: typography.sizes.base,
      color: colors.text,
      minHeight: 48,
    },
    profileSheetSettingsLink: {
      marginTop: spacing.xl,
      alignItems: 'center',
      paddingVertical: spacing.md,
    },
    profileSheetSettingsLinkText: {
      fontSize: typography.sizes.base,
      color: colors.accent,
      fontWeight: typography.weights.medium,
    },
  });
}
